// src/components/Sidebar.tsx
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { NavLink, useParams } from "react-router-dom";

import { CustomerIcon } from "../icons/CustomerIcon";
import { InventoryIcon } from "../icons/InventoryIcon";
import { ReportIcon } from "../icons/ReportIcon";
import { PurchaseIcon } from "../icons/PurchaseIcon";
import { DollarIcon } from "../icons/DollarIcon";
import { ReceivableIcon } from "../icons/ReceivableIcon";
import { PayableIcon } from "../icons/PayableIcon";
import { SettingsIcon } from "../icons/SettingsIcon";
import { ArrowsRightLeftIcon } from "@heroicons/react/24/outline";
import {
  HomeIcon,
  ReceiptPercentIcon,
  ClipboardDocumentListIcon,
  BuildingStorefrontIcon,
  BookOpenIcon,
  PencilSquareIcon,
  CameraIcon,
} from "@heroicons/react/24/outline";

import { AuthMe } from "../types/auth";
import ProBadge from "./ProBadge";
import { A } from "../styles/appleClasses";

// company-aware links
function companyRoute(company_id: string | undefined, path: string): string {
  if (!company_id) return path;
  return `/company/${company_id}${path.startsWith("/") ? path : "/" + path}`;
}

type SidebarLink = {
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  route: string;
  /** If true, staff-POS users can see this entry; otherwise admin-only. */
  staffAllowed?: boolean;
  /** If false, nested routes keep the item active. */
  end?: boolean;
};

const getSidebarSections = (t: TFunction): { id: string; label: string; links: SidebarLink[] }[] => [
  {
    id: "overview",
    label: t("navigation.sections.overview"),
    links: [
      {
        label: t("navigation.home"),
        icon: HomeIcon,
        route: "/dashboard",
        staffAllowed: true,
      },
      {
        label: t("navigation.scanner"),
        icon: CameraIcon,
        route: "/scanner",
      },
    ],
  },
  {
    id: "salesPurchases",
    label: t("navigation.sections.salesPurchases"),
    links: [
      {
        label: t("navigation.sales"),
        icon: ReceiptPercentIcon,
        route: "/sales",
        staffAllowed: true,
      },
      {
        label: t("navigation.enterSale"),
        icon: DollarIcon,
        route: "/sales/new",
        staffAllowed: true,
      },
      {
        label: t("navigation.purchases"),
        icon: ClipboardDocumentListIcon,
        route: "/purchases",
      },
      { label: t("navigation.enterPurchase"), icon: PurchaseIcon, route: "/purchases/new" },
    ],
  },
  {
    id: "expensesOthers",
    label: t("navigation.sections.expensesOthers"),
    links: [
      {
        label: t("navigation.enterExpense"),
        icon: PencilSquareIcon,
        route: "/expenses/new",
      },
      {
        label: t("navigation.spending"),
        icon: ClipboardDocumentListIcon,
        route: "/recognition",
      },
    ],
  },
  {
    id: "receivablesPayables",
    label: t("navigation.sections.receivablesPayables"),
    links: [
      { label: t("navigation.receivables"), icon: ReceivableIcon, route: "/receivables" },
      { label: t("navigation.payables"), icon: PayableIcon, route: "/payables" },
    ],
  },
  {
    id: "directory",
    label: t("navigation.sections.directory"),
    links: [
      {
        label: t("navigation.customers"),
        icon: CustomerIcon,
        route: "/customers",
        staffAllowed: true,
      },
      { label: t("navigation.vendors"), icon: BuildingStorefrontIcon, route: "/vendors" },
      {
        label: t("navigation.products"),
        icon: InventoryIcon,
        route: "/products",
        staffAllowed: true,
      },
    ],
  },
  {
    id: "cashEquity",
    label: t("navigation.sections.cashEquity"),
    links: [
      {
        label: t("navigation.enterAdvanceWithdrawal"),
        icon: ArrowsRightLeftIcon,
        route: "/owner-funds/new",
      },
      { label: t("navigation.advanceWithdrawal"), icon: DollarIcon, route: "/owner-funds" },
    ],
  },
  {
    id: "reportsSettings",
    label: t("navigation.sections.reportsSettings"),
    links: [
      { label: t("navigation.reports"), icon: ReportIcon, route: "/reports" },
      { label: t("navigation.numbers"), icon: BookOpenIcon, route: "/books" },
      { label: t("navigation.settings"), icon: SettingsIcon, route: "/settings" },
    ],
  },
];

export default function Sidebar({ user }: { user?: AuthMe }) {
  const { t } = useTranslation();
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const { company_id } = useParams<{ company_id?: string }>();

  const isCompanyAdmin = user?.is_company_admin === true;
  const isSuperAdmin = user?.is_ricebook_admin === true;
  const isStaffPOS = !!user && !isCompanyAdmin && !isSuperAdmin;

  const iconBtn =
    `md:hidden fixed z-40 inline-flex items-center justify-center ${A.iconBtn} ` +
    "bg-white border border-gray-200 shadow-sm " +
    "hover:bg-gray-50 transition " +
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-appleblueD";

  // ✅ Standard: icon + text are neutral until hover; active = appleblue
  const iconSizeCls = "w-5 h-5 shrink-0 !text-inherit"; // forces SVG to inherit parent color

  return (
    <>
      {/* Hamburger (mobile) */}
      <button
        className={`${iconBtn} top-5 left-4`}
        onClick={() => setSidebarOpen(true)}
        aria-label={t("navigation.openMenu")}
        type="button"
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          className="text-appleblue"
          aria-hidden="true"
        >
          <rect y="5" width="24" height="2" rx="1" fill="currentColor" />
          <rect y="11" width="24" height="2" rx="1" fill="currentColor" />
          <rect y="17" width="24" height="2" rx="1" fill="currentColor" />
        </svg>
      </button>

      {/* Overlay (mobile) */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-[1000] bg-black/20 md:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden
        />
      )}

      {/* Sidebar panel */}
      <aside
        className={`
    fixed z-[1010] top-0 left-0 w-64 bg-white border-r border-gray-200 shadow-sm flex flex-col
    h-[100dvh] overflow-y-auto pb-10
    transition-transform duration-200
    ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"}
    md:translate-x-0 md:static md:block
  `}
      >
        {/* Header / brand row */}
        <div className="sticky top-0 z-20 bg-white flex items-center justify-between gap-3 py-3 px-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-appleblue/10 text-appleblue">
              <svg
                width="26"
                height="26"
                viewBox="0 0 32 32"
                fill="none"
                aria-hidden="true"
              >
                <rect
                  x="6"
                  y="6"
                  width="20"
                  height="20"
                  rx="4"
                  stroke="currentColor"
                  strokeWidth="2.2"
                />
                <path
                  d="M12 10h8M12 14h8M12 18h6"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>

            <span className="text-[15px] font-semibold tracking-tight text-[#D32373]">
              nimos
            </span>
          </div>

          <ProBadge show={user?.is_user_pro === true} size="xs" />
        </div>

        {/* Nav sections */}
        <nav className="flex flex-col gap-0 px-2 py-2">
          {getSidebarSections(t).map((section, idx) => {
            const visibleLinks = section.links.filter((item) => {
              if (!user) return true; // user loading → show all
              if (!isStaffPOS) return true; // admin/super-admin → show all
              return !!item.staffAllowed; // staff POS → only allowed
            });

            if (visibleLinks.length === 0) return null;

            return (
              <React.Fragment key={section.id}>
                {idx > 0 && <hr className="my-2 border-gray-100" />}

                <div className="pl-2 pb-1 text-[11px] text-gray-400 tracking-wider font-medium uppercase select-none">
                  {section.label}
                </div>

                {visibleLinks.map((item) => {
                  const Icon = item.icon;
                  const to = item.route.startsWith("/admin")
                    ? item.route
                    : companyRoute(company_id, item.route);

                  const baseLink =
                    "group flex items-center gap-3 px-4 py-2 rounded-xl transition " +
                    "text-[13px] font-medium " +
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-appleblueD focus-visible:ring-inset";

                  return (
                    <NavLink
                      key={item.route}
                      to={to}
                      end={item.end ?? true}
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        [
                          baseLink,
                          isActive
                            ? "bg-appleblue/10 text-appleblue"
                            : "text-gray-700 hover:bg-gray-50 hover:text-appleblue",
                        ].join(" ")
                      }
                    >
                      {({ isActive }) => (
                        <>
                          {/* ✅ Force ALL icons to follow the same color rules (even custom icons) */}
                          <span
                            className={[
                              "shrink-0",
                              isActive
                                ? "text-appleblue"
                                : "text-gray-400 group-hover:text-appleblue",
                            ].join(" ")}
                          >
                            <Icon className={iconSizeCls} aria-hidden="true" />
                          </span>

                          <span className="min-w-0 truncate">{item.label}</span>

                        </>
                      )}
                    </NavLink>
                  );
                })}
              </React.Fragment>
            );
          })}
        </nav>

        {/* Admin-only section (platform-level) */}
        {user?.is_ricebook_admin && (
          <>
            <hr className="my-2 border-gray-100" />
            <div className="pl-2 pb-1 text-[11px] text-gray-400 tracking-wider font-medium uppercase select-none">
              {t("navigation.admin")}
            </div>

            <NavLink
              to={companyRoute(company_id, "/admin/users")}
              end
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                [
                  "group flex items-center gap-3 px-4 py-2 rounded-xl transition text-[13px] font-medium",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-appleblueD focus-visible:ring-inset",
                  isActive
                    ? "bg-appleblue/10 text-appleblue"
                    : "text-gray-700 hover:bg-gray-50 hover:text-appleblue",
                ].join(" ")
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className={[
                      "shrink-0",
                      isActive
                        ? "text-appleblue"
                        : "text-gray-400 group-hover:text-appleblue",
                    ].join(" ")}
                  >
                    <SettingsIcon className={iconSizeCls} aria-hidden="true" />
                  </span>
                  <span className="min-w-0 truncate">{t("navigation.adminPanel")}</span>
                </>
              )}
            </NavLink>
          </>
        )}

        <div className="flex-1" />
      </aside>

      {/* Close (mobile) */}
      <button
        className={`${iconBtn} top-5 right-4`}
        style={{ display: isSidebarOpen ? "inline-flex" : "none" }}
        onClick={() => setSidebarOpen(false)}
        aria-label={t("navigation.closeMenu")}
        type="button"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          className="text-appleblue"
          aria-hidden="true"
        >
          <line
            x1="6"
            y1="6"
            x2="18"
            y2="18"
            stroke="currentColor"
            strokeWidth="2"
          />
          <line
            x1="18"
            y1="6"
            x2="6"
            y2="18"
            stroke="currentColor"
            strokeWidth="2"
          />
        </svg>
      </button>
    </>
  );
}
